/*
 * BodyTypeSelector — 6 minimalist body silhouette icons for fit guidance.
 * Selection is UI-only (does not affect price/inventory) but is persisted
 * with the cart line as a note. Icons are simple line-art in viewBox 0 0 24 32.
 */

export const BODY_TYPES = [
  {
    id: "athletic",
    label: "Athletic",
    hint: "Broad shoulders, narrow waist",
    render: () => (
      <>
        <circle cx="12" cy="5" r="2.5" />
        <polygon points="4,10 20,10 14,26 10,26" />
      </>
    ),
  },
  {
    id: "standard",
    label: "Standard",
    hint: "Balanced proportions",
    render: () => (
      <>
        <circle cx="12" cy="5" r="2.5" />
        <polygon points="7,10 17,10 16,26 8,26" />
      </>
    ),
  },
  {
    id: "broad-shoulder",
    label: "Broad Shoulder",
    hint: "Wide shoulders, narrower hips",
    render: () => (
      <>
        <circle cx="12" cy="5" r="2.5" />
        <polygon points="3,10 21,10 13,26 11,26" />
      </>
    ),
  },
  {
    id: "belly",
    label: "Belly / Midsection",
    hint: "Rounder mid-section",
    render: () => (
      <>
        <circle cx="12" cy="5" r="2.5" />
        <path d="M7,10 L17,10 L18,15 Q21,20 18,23 L17,26 L7,26 L6,23 Q3,20 6,15 Z" />
      </>
    ),
  },
  {
    id: "love-handles",
    label: "Love Handles",
    hint: "Wider at waist / hips",
    render: () => (
      <>
        <circle cx="12" cy="5" r="2.5" />
        <polygon points="8,10 16,10 19,22 17,26 7,26 5,22" />
      </>
    ),
  },
  {
    id: "chest-heavy",
    label: "Chest Heavy",
    hint: "Broader chest, tapered waist",
    render: () => (
      <>
        <circle cx="12" cy="5" r="2.5" />
        <path d="M6,10 L18,10 L21,14 Q19,17 16,18 L15,26 L9,26 L8,18 Q5,17 3,14 Z" />
      </>
    ),
  },
];

export default function BodyTypeSelector({ value, onChange }) {
  return (
    <div data-testid="body-type-selector">
      <div className="mt-8 flex items-center justify-between">
        <span className="text-[11px] tracking-[0.22em] uppercase text-[#000000]/70">
          Body Type
        </span>
        {value ? (
          <span
            data-testid="body-type-selected-label"
            className="text-[10px] tracking-[0.18em] uppercase text-[#c9a84c]"
          >
            {BODY_TYPES.find((b) => b.id === value)?.label}
          </span>
        ) : (
          <span className="text-[10px] tracking-[0.18em] uppercase text-[#000000]/40">
            Optional
          </span>
        )}
      </div>

      <div
        className="grid grid-cols-6 gap-2 mt-3"
        role="radiogroup"
        aria-label="Body type"
      >
        {BODY_TYPES.map((b) => {
          const active = value === b.id;
          return (
            <button
              key={b.id}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={b.label}
              title={`${b.label} — ${b.hint}`}
              data-testid={`body-type-option-${b.id}`}
              onClick={() => onChange(active ? null : b.id)}
              className={`aspect-square flex items-center justify-center border transition-colors ${
                active
                  ? "border-[#c9a84c] bg-[#c9a84c]/[0.06]"
                  : "border-[#000000]/20 hover:border-[#000000]/60"
              }`}
            >
              <svg
                viewBox="0 0 24 32"
                width="26"
                height="34"
                fill="none"
                stroke={active ? "#c9a84c" : "#0a0a0a"}
                strokeWidth="1.2"
                strokeLinejoin="round"
                strokeLinecap="round"
                aria-hidden="true"
              >
                {b.render()}
              </svg>
            </button>
          );
        })}
      </div>

      <p className="text-[10px] tracking-[0.22em] uppercase text-[#000000]/40 mt-2">
        Helps us confirm the fit — no impact on price.
      </p>
    </div>
  );
}
